/**
 * "Send Link To Applicant" (design §12), opened from the kebab's "Send Update
 * Link" item (gated on `can.sendUpdateLink`). Two actions via a radio
 * (V2-GAPS-AND-PDF-PLAN §B/§C):
 *   - **Send Update Link** → `{ intent: 'sendUpdateLink', applicationIds, notes }`
 *     (emails the applicant a link to revisit/resume their screening).
 *   - **Resend Screening Invitation** → `{ intent: 'resendInvitation',
 *     applicationIds, groupId }` (re-emails an invited co-applicant/guarantor's
 *     invitation; superadmin-only server-side — primaries have no invitation
 *     so the handler 404s that selection).
 * The applicant picker is a multi-select chip dropdown (design): each selected
 * member becomes a removable avatar chip and the action fans out across every
 * selected `applicationId` (the server loops the comma list — actions.server.ts).
 */
import { ArrowRight, Check, ChevronDown, X } from 'lucide-react'
import { useState } from 'react'
import { useFetcher } from 'react-router'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu'
import { applicationTypeLabel } from '#app/utils/application-detail-v2/display'
import type {
	ApplicationSummary,
	DetailActionResult,
	GroupMember,
} from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { PastelChip } from '../primitives'
import {
	ActionResultMarker,
	DetailDialog,
	FloatingField,
	InlineActionError,
	PillButton,
} from './dialog-bits'

/** Two-letter initials from a display name (first + last word). */
function initials(name: string) {
	const parts = name.trim().split(/\s+/).filter(Boolean)
	if (parts.length === 0) return '?'
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Pale-green initials circle with the design's small presence dot. */
function ApplicantAvatar({
	name,
	className,
}: {
	name: string
	className?: string
}) {
	return (
		<span
			className={cn(
				'relative inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-[#F0FDF4] text-[11px] font-semibold text-[#16A34A]',
				className,
			)}
		>
			{initials(name)}
			<span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-[#16A34A] ring-2 ring-white" />
		</span>
	)
}

interface ApplicantOption {
	id: string
	name: string
	badge: string
	guarantor: boolean
}

export function SendUpdateLinkDialog({
	open,
	onOpenChange,
	application,
	groupMembers,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
	groupMembers: GroupMember[]
}) {
	const fetcher = useFetcher<DetailActionResult>()
	const [selectedIds, setSelectedIds] = useState<string[]>([application.id])
	const [action, setAction] = useState<'update-link' | 'resend-invitation'>(
		'update-link',
	)
	const [notes, setNotes] = useState('')
	const busy = fetcher.state !== 'idle'
	const isResend = action === 'resend-invitation'

	// Reset the fields on EVERY close (success or cancel) — DecisionDialog
	// resets its note the same way.
	const handleOpenChange = (next: boolean) => {
		if (!next) {
			setNotes('')
			setSelectedIds([application.id])
			setAction('update-link')
		}
		onOpenChange(next)
	}

	// Group members carry their own applicationId; solo applications fall
	// back to the page's applicant (the DTO has no other candidates).
	const options: ApplicantOption[] =
		groupMembers.length > 0
			? groupMembers.map((member: GroupMember) => ({
					id: member.applicationId,
					name: member.displayName,
					badge: member.relationshipLabel,
					guarantor: member.applicationType === 'guarantor',
				}))
			: [
					{
						id: application.id,
						name: application.applicantName,
						badge: applicationTypeLabel(application.applicationType),
						guarantor: application.applicationType === 'guarantor',
					},
				]

	const selectedOptions = options.filter((o) => selectedIds.includes(o.id))

	const toggle = (id: string) => {
		setSelectedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		)
	}

	return (
		<>
			<ActionResultMarker
				fetcher={fetcher}
				fallback="Application link sent"
				onSuccess={() => handleOpenChange(false)}
			/>
			<DetailDialog
				open={open}
				onOpenChange={handleOpenChange}
				title="Send Link To Applicant"
				description="Send an application update link or resend a screening invitation."
				className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-xl"
			>
				<FloatingField label="Applicant(s)" className="mt-3">
					{/* modal={false}: this menu lives inside a modal Dialog. A modal
					    dropdown adds its own body-level pointer/focus guards, and
					    closing it (the 2nd trigger click) then gets read by the Dialog
					    as an interact-outside — closing the whole modal. Non-modal
					    keeps the dropdown scoped to the dialog's layer. */}
					<DropdownMenu modal={false}>
						<DropdownMenuTrigger asChild disabled={busy}>
							<div
								role="button"
								tabIndex={0}
								aria-label="Select applicants"
								className="flex w-full cursor-pointer flex-wrap items-center gap-2 outline-none"
							>
								{selectedOptions.length > 0 ? (
									selectedOptions.map((option) => (
										<span
											key={option.id}
											className="inline-flex items-center gap-1.5 rounded-full border border-[#F0F0F0] bg-white p-2 text-[14px] text-[#32302F]"
										>
											<ApplicantAvatar
												name={option.name}
												className="size-6 text-[10px]"
											/>
											<span className="max-w-[140px] truncate">
												{option.name}
											</span>
											<button
												type="button"
												aria-label={`Remove ${option.name}`}
												disabled={busy}
												onPointerDown={(event) => {
													// preventDefault keeps focus OFF this button.
													// Otherwise it takes focus, then `toggle` unmounts
													// it, focus falls to <body> (outside the dialog),
													// and Radix reads that focusout as an
													// interact-outside — closing the whole dialog.
													// stopPropagation keeps the click from opening the
													// dropdown.
													event.preventDefault()
													event.stopPropagation()
												}}
												onClick={(event) => {
													event.stopPropagation()
													toggle(option.id)
												}}
												className="text-[#32302F] hover:text-gray-600"
											>
												<X className="size-3.5" />
											</button>
										</span>
									))
								) : (
									<span className="text-[14px] text-[#CCCCCC]">
										Select Applicant
									</span>
								)}
								<ChevronDown className="ml-auto size-4 shrink-0 text-gray-500" />
							</div>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="start"
							sideOffset={12}
							className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-2xl border-[#ECECEA] bg-white p-2 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)]"
						>
							{options.map((option) => {
								const checked = selectedIds.includes(option.id)
								return (
									<DropdownMenuItem
										key={option.id}
										onSelect={(event) => {
											// Keep the menu open for multi-select.
											event.preventDefault()
											toggle(option.id)
										}}
										className="flex cursor-pointer items-center justify-between gap-3 rounded-xl px-2 py-2 focus:bg-[#F6F7F5]"
									>
										<span className="flex min-w-0 items-center gap-2.5">
											<ApplicantAvatar name={option.name} />
											<span className="font-Regular truncate text-[15px] text-[#32302F]">
												{option.name}
											</span>
										</span>
										<span className="flex shrink-0 items-center gap-2">
											<PastelChip
												tone={option.guarantor ? 'damber' : 'dgreen'}
												label={option.badge}
												className="px-3 py-1 text-[12px]"
											/>
											{checked ? (
												<Check className="size-4 text-[#16A34A]" />
											) : null}
										</span>
									</DropdownMenuItem>
								)
							})}
						</DropdownMenuContent>
					</DropdownMenu>
				</FloatingField>

				<div>
					<p className="text-[16px] font-semibold text-[#32302F]">Action</p>
					<label className="mt-3 flex w-fit cursor-pointer items-center gap-2.5">
						<input
							type="radio"
							name="send-link-action"
							value="update-link"
							checked={!isResend}
							onChange={() => setAction('update-link')}
							disabled={busy}
							className="size-4 accent-[#1F1E1D]"
						/>
						<span className="text-[16px] text-[#32302F]">Send Update Link</span>
					</label>
					<label className="mt-2.5 flex w-fit cursor-pointer items-center gap-2.5">
						<input
							type="radio"
							name="send-link-action"
							value="resend-invitation"
							checked={isResend}
							onChange={() => setAction('resend-invitation')}
							disabled={busy}
							className="size-4 accent-[#1F1E1D]"
						/>
						<span className="text-[16px] text-[#32302F]">
							Resend Screening Invitation
						</span>
					</label>
					<p className="mt-3 text-[15px] text-[#747474] leading-5">
						{isResend
							? 'Re-email the screening invitation to an invited co-applicant or guarantor.'
							: 'Send a link so the applicant can update their screening application details.'}
					</p>
				</div>

				{!isResend ? (
					<FloatingField
						label="Notes (Optional)"
						htmlFor="send-link-notes"
						className="mt-2"
					>
						<textarea
							id="send-link-notes"
							rows={4}
							value={notes}
							onChange={(event) => setNotes(event.target.value)}
							disabled={busy}
							placeholder="Add A Note For The Applicant..."
							className="w-full resize-none bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[14px] placeholder:text-[#CCCCCC]"
						/>
					</FloatingField>
				) : null}

				<InlineActionError fetcher={fetcher} open={open} />

				<div className="mt-2 flex justify-end">
					<PillButton
						tone="dark"
						disabled={busy || selectedIds.length === 0}
						onClick={() => {
							const applicationIds = selectedIds.join(',')
							const form: Record<string, string> = isResend
								? { intent: 'resendInvitation', applicationIds }
								: { intent: 'sendUpdateLink', applicationIds, notes }
							if (isResend && application.groupId) {
								form.groupId = application.groupId
							}
							void fetcher.submit(form, { method: 'post' })
						}}
					>
						{busy
							? 'Sending...'
							: isResend
								? 'Resend Invitation'
								: 'Send Update Link'}
						<ArrowRight className="size-4" />
					</PillButton>
				</div>
			</DetailDialog>
		</>
	)
}
